import { Injectable, effect, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';

import { DisplaySlotLocalizer } from '../hgm-setup/src/module/param/services/display-slot-localizer';
import { ParamDefDisplaySlotLocalizer } from '../hgm-setup/src/module/param/services/param-def-display-slot-localizer';

const slotL10nKey = 'slotL10nSelections';

interface SettingDef<T> {
    storeKey?: string;
    default: T;
}

export type XAxisType = 'relative' | 'time' | 'date';
export type ChartDivPolicy = 'single' | 'perSlot' | 'perSignal';
export type ThemePolicy = 'system' | 'light' | 'dark';

const defs = {
    xAxisType: { default: 'relative' } as SettingDef<XAxisType>,
    preferredFineWidth: { default: 10 * 1000 } as SettingDef<number>,
    preferredCoarseWidth: { default: 5 * 60 * 1000 } as SettingDef<number>,
    alwaysShowCoarse: { default: false } as SettingDef<boolean>,
    chartDivPolicy: { default: 'perSlot' } as SettingDef<ChartDivPolicy>,
    themePolicy: { default: 'system' } as SettingDef<ThemePolicy>,
} as const;

@Injectable({ providedIn: 'root' })
export class GlobalSettingsProvider {
    xAxisType = signal(defs.xAxisType.default);
    xAxisTypeObs: Observable<typeof defs.xAxisType.default>;
    preferredFineWidth = signal(defs.preferredFineWidth.default);
    preferredFineWidthObs: Observable<typeof defs.preferredFineWidth.default>;
    preferredCoarseWidth = signal(defs.preferredCoarseWidth.default);
    preferredCoarseWidthObs: Observable<typeof defs.preferredCoarseWidth.default>;
    alwaysShowCoarse = signal(defs.alwaysShowCoarse.default);
    alwaysShowCoarseObs: Observable<typeof defs.alwaysShowCoarse.default>;
    chartDivPolicy = signal(defs.chartDivPolicy.default);
    chartDivPolicyObs: Observable<typeof defs.chartDivPolicy.default>;
    themePolicy = signal(defs.themePolicy.default);
    themePolicyObs: Observable<typeof defs.themePolicy.default>;

    private readonly dsl = inject(DisplaySlotLocalizer);

    constructor() {
        this.paramDsl.ready().subscribe({
            next: () => {
                this.paramDsl.indices = this.savedIndices;
            },
        });
        for (const [key, def] of Object.entries(defs)) {
            const stored = window.localStorage.getItem(def.storeKey ?? key);
            let value = def.default;
            if (stored !== null) {
                try {
                    value = JSON.parse(stored) ?? def.default;
                } catch {}
            }
            if (key === 'chartDivPolicy' && (value as any) === 'perUnit') {
                value = 'perSlot' as any;
            }
            (this as any)[key].set(value);
            effect(() => {
                const v = (this as any)[key]();
                window.localStorage.setItem(def.storeKey ?? key, JSON.stringify(v));
            });
            (this as any)[key + 'Obs'] = toObservable((this as any)[key]);
        }
    }

    onUnitSelectionChange() {
        // keep any indices that don't appear in the presently active log
        const toSave = {
            ...this.savedIndices,
            ...this.paramDsl.indices,
        };
        window.localStorage.setItem(slotL10nKey, JSON.stringify(toSave));
    }

    private get paramDsl() {
        return this.dsl as ParamDefDisplaySlotLocalizer;
    }

    private get savedIndices() {
        const saved = JSON.parse(window.localStorage.getItem(slotL10nKey) || '{}');
        return saved && typeof saved === 'object' ? saved : {};
    }
}
